import { allKey } from 'app/constants'
import ListTable from 'core/components/listTable/ListTable'
import useParams from 'core/hooks/useParams'
import NamespacePicklist from 'k8s/components/common/NamespacePicklist'
import React, { FC, useCallback, useMemo, useState } from 'react'
import EditIcon from 'k8s/components/rbac/profiles/EditIcon'
import DeleteIcon from 'k8s/components/rbac/profiles/DeleteIcon'
import ViewIcon from 'k8s/components/rbac/profiles/ViewIcon'
import useStyles from 'k8s/components/rbac/profiles/edit/useStyles'
import { propEq } from 'ramda'
import { IRbacNamespaceScopedRoles, IRbacRoleBinding } from 'k8s/components/rbac/model'
import Button from 'core/elements/button'
import useToggler from 'core/hooks/useToggler'
import EditRoleBindingDialog from 'k8s/components/rbac/profiles/edit/EditRoleBindingDialog'
import ViewRoleBindingDialog from 'k8s/components/rbac/profiles/edit/ViewRoleBindingDialog'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'

const infoColumns = [
  { id: 'metadata.name', label: 'Name' },
  { id: 'metadata.namespace', label: 'Namespace' },
]
const visibleColumns = ['metadata.name', 'metadata.namespace', 'apiAccess']
const columnsOrder = ['metadata.name', 'metadata.namespace', 'apiAccess']
const orderBy = 'metadata.name'
const orderDirection = 'asc'
const searchTargets = ['metadata.name', 'metadata.namespace']

interface Props {
  profileName: string
  clusterId: string
  namespaceScopedRoles: IRbacNamespaceScopedRoles[]
  onAdd: (role: IRbacRoleBinding) => void
  onUpdate: (role: IRbacRoleBinding) => void
  onRemove: (role: IRbacRoleBinding) => void
  readOnly?: boolean
}

const RoleBindingsList: FC<Props> = ({
  profileName,
  clusterId,
  namespaceScopedRoles,
  onRemove,
  onUpdate,
  onAdd,
  readOnly = false,
}) => {
  /*****************************
   *      EDIT DIALOG
   *****************************/
  const [currentRow, setCurrentRow] = useState<IRbacRoleBinding>()
  const [showingEditDialog, toggleEditDialog] = useToggler()
  const showEditDialog = useCallback((row: IRbacRoleBinding) => {
    setCurrentRow(row)
    toggleEditDialog()
  }, [])
  const handleUpdate = useCallback(
    (roleBinding: IRbacRoleBinding) => {
      onUpdate(roleBinding)
      toggleEditDialog()
    },
    [onUpdate],
  )

  /*****************************
   *        ADD DIALOG
   *****************************/
  const handleAdd = useCallback(() => {
    toggleAddDialog()
  }, [])
  const addToProfile = useCallback(
    (newRole: IRbacRoleBinding) => {
      toggleAddDialog()
      onAdd(newRole)
    },
    [onAdd],
  )
  const [showingAddDialog, toggleAddDialog] = useToggler()

  /*****************************
   *        VIEW DIALOG
   *****************************/
  const [showingViewDialog, toggleViewDialog] = useToggler()
  const showViewRoleDialog = useCallback((row: IRbacRoleBinding) => {
    setCurrentRow(row)
    toggleViewDialog()
  }, [])

  /*****************************
   *    ROLE BINDINGS LIST
   *****************************/
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams({
    // masterNodeClusters: true,
    clusterId: allKey,
    namespace: allKey,
  })

  const visibleRoles: IRbacRoleBinding[] = useMemo(
    () =>
      params.namespace && params.namespace !== allKey
        ? namespaceScopedRoles.find(propEq('name', params.namespace))?.roleBindings || []
        : namespaceScopedRoles.reduce(
            (allRoleBindings, currentNamespace) => [
              ...allRoleBindings,
              ...(currentNamespace?.roleBindings || []),
            ],
            [],
          ),
    [params.namespace, namespaceScopedRoles],
  )

  const columns = useMemo(
    () =>
      readOnly
        ? [
            ...infoColumns,
            {
              id: 'apiAccess',
              render: (value, row) => (
                <ViewIcon
                  onClick={(e) => {
                    e.stopPropagation()
                    showViewRoleDialog(row)
                  }}
                />
              ),
            },
          ]
        : [
            ...infoColumns,
            // Actions column
            {
              id: 'apiAccess',
              render: (value, row) => (
                <>
                  <EditIcon
                    onClick={(e) => {
                      e.stopPropagation()
                      showEditDialog(row)
                    }}
                  />
                  <DeleteIcon
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(row)
                    }}
                  />
                </>
              ),
            },
          ],
    [onRemove, readOnly],
  )

  return (
    <>
      {!readOnly && (
        <HeaderPrimaryActionPortal>
          <Button className={classes.addBtn} onClick={handleAdd} icon="plus">
            Add New Role Binding
          </Button>
        </HeaderPrimaryActionPortal>
      )}
      {showingViewDialog && (
        <ViewRoleBindingDialog
          onClose={toggleViewDialog}
          open={showingViewDialog}
          roleBinding={currentRow}
        />
      )}
      {showingAddDialog && (
        <EditRoleBindingDialog
          clusterId={clusterId}
          profileName={profileName}
          onClose={toggleAddDialog}
          onSubmit={addToProfile}
          open={showingAddDialog}
        />
      )}
      {currentRow && showingEditDialog && (
        <EditRoleBindingDialog
          clusterId={clusterId}
          profileName={profileName}
          onClose={toggleEditDialog}
          onSubmit={handleUpdate}
          open={showingEditDialog}
          roleBinding={currentRow}
        />
      )}
      <ListTable
        compact
        className={classes.listTable}
        filters={
          <NamespacePicklist
            /*
            // @ts-ignore */
            value={params.namespace}
            clusterId={clusterId}
            disabled={!clusterId}
            onChange={getParamsUpdater('namespace')}
          />
        }
        showPagination
        uniqueIdentifier={({ metadata }) => `${metadata.name}-${metadata.namespace}`}
        canEditColumns={false}
        searchTargets={searchTargets}
        showCheckboxes={false}
        data={visibleRoles}
        columns={columns}
        visibleColumns={visibleColumns}
        columnsOrder={columnsOrder}
        orderBy={orderBy}
        orderDirection={orderDirection}
      />
    </>
  )
}

export default RoleBindingsList
